import { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { createLocalStorageSync } from './hooks/use-local-storage-sync'
import { useScope } from './scope-context'
import { useAuth } from './auth-context'
import type { PaidByFilter } from './utils'

type PaidByContextValue = {
  paidBy: PaidByFilter
  setPaidBy: (s: PaidByFilter) => void
}

export const PaidByContext = createContext<PaidByContextValue | null>(null)

const usePaidByStorage = createLocalStorageSync<PaidByFilter>(
  'paid-by-preference',
  'all',
  (raw) => (typeof raw === 'string' && raw.length > 0 ? raw : null),
)

export function PaidByProvider({ children }: { children: React.ReactNode }) {
  const [paidBy, setPaidBy] = usePaidByStorage()
  const { scope } = useScope()
  const { user: currentUser } = useAuth()
  const prevScopeRef = useRef(scope)

  useEffect(() => {
    if (prevScopeRef.current === scope) return
    prevScopeRef.current = scope

    if (scope === 'personal' && currentUser) {
      setPaidBy(currentUser.id)
    } else if (scope !== 'personal') {
      setPaidBy('all')
    }
  }, [scope, currentUser, setPaidBy])

  const value = useMemo<PaidByContextValue>(
    () => ({ paidBy, setPaidBy }),
    [paidBy, setPaidBy],
  )

  return (
    <PaidByContext.Provider value={value}>
      {children}
    </PaidByContext.Provider>
  )
}

export function usePaidBy(): PaidByContextValue {
  const ctx = useContext(PaidByContext)
  if (!ctx) throw new Error('usePaidBy must be used within PaidByProvider')
  return ctx
}
